import React, { useState, useEffect } from 'react';
import { db, storage } from '../../../utils/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, updateDoc, doc, deleteDoc, getDocs, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { Plus, Edit2, Trash2, Check, X, Loader, Upload, Image as ImageIcon, Eye, EyeOff } from 'lucide-react';
import { MagazineContent, Product, LookbookImage } from '../../../types';
import ImageUploader from '../common/ImageUploader';

const MagazineManager: React.FC = () => {
  const [contents, setContents] = useState<MagazineContent[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Form State
  const [title, setTitle] = useState('');
  const [type, setType] = useState<'lookbook' | 'editorial' | 'campaign' | 'runway'>('lookbook');
  const [season, setSeason] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [description, setDescription] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [author, setAuthor] = useState('');
  const [featured, setFeatured] = useState(false);
  const [visible, setVisible] = useState(true);

  // Lookbook-specific state
  const [lookbookImages, setLookbookImages] = useState<LookbookImage[]>([]);

  // Editorial/Campaign state
  const [images, setImages] = useState<string[]>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);

  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch Magazine Content
      const contentQuery = query(collection(db, 'magazine'), orderBy('createdAt', 'desc'));
      const contentSnap = await getDocs(contentQuery);
      const fetchedContents = contentSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as MagazineContent));
      setContents(fetchedContents);

      // Fetch Products (for selection)
      const pQuery = query(collection(db, 'products'), orderBy('title', 'asc'));
      const pSnap = await getDocs(pQuery);
      const fetchedProducts = pSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Product));
      setProducts(fetchedProducts);

    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const newImageUrls: string[] = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const storageRef = ref(storage, `magazine/${type}/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        newImageUrls.push(url);
      }

      if (type === 'lookbook' || type === 'runway') {
        // Add to lookbook images
        const newLookbookImages: LookbookImage[] = newImageUrls.map(url => ({
          url,
          description: '',
          productIds: []
        }));
        setLookbookImages(prev => [...prev, ...newLookbookImages]);
      } else {
        // Add to regular images array
        setImages(prev => [...prev, ...newImageUrls]);
      }
    } catch (error) {
      console.error("Error uploading images:", error);
      setMessage({ type: 'error', text: 'Failed to upload images.' });
    } finally {
      setUploading(false);
    }
  };

  const handleLookbookImageReplace = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const storageRef = ref(storage, `magazine/${type}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      setLookbookImages(prev => prev.map((img, i) =>
        i === index ? { ...img, url } : img
      ));
    } catch (error) {
      console.error("Error replacing image:", error);
      setMessage({ type: 'error', text: 'Failed to replace image.' });
    } finally {
      setUploading(false);
    }
  };

  const removeLookbookImage = (index: number) => {
    setLookbookImages(prev => prev.filter((_, i) => i !== index));
  };

  const updateLookbookImageDescription = (index: number, description: string) => {
    setLookbookImages(prev => prev.map((img, i) =>
      i === index ? { ...img, description } : img
    ));
  };

  const toggleLookbookImageProduct = (imageIndex: number, productId: string) => {
    setLookbookImages(prev => prev.map((img, i) => {
      if (i !== imageIndex) return img;
      const productIds = img.productIds || [];
      return {
        ...img,
        productIds: productIds.includes(productId)
          ? productIds.filter(id => id !== productId)
          : [...productIds, productId]
      };
    }));
  };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const toggleProductSelection = (productId: string) => {
    setSelectedProductIds(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const resetForm = () => {
    setTitle('');
    setType('lookbook');
    setSeason('');
    setSubtitle('');
    setDescription('');
    setCoverImage('');
    setAuthor('');
    setFeatured(false);
    setVisible(true);
    setLookbookImages([]);
    setImages([]);
    setSelectedProductIds([]);
    setEditingId(null);
    setMessage(null);
  };

  const handleEdit = (content: MagazineContent) => {
    setEditingId(content.id);
    setTitle(content.title);
    setType(content.type);
    setSeason(content.season || '');
    setSubtitle(content.subtitle || '');
    setDescription(content.description);
    setCoverImage(content.coverImage);
    setAuthor(content.author || '');
    setFeatured(content.featured);
    setVisible(content.visible);

    if ((content.type === 'lookbook' || content.type === 'runway') && content.lookbookImages) {
      setLookbookImages(content.lookbookImages);
      setImages([]);
      setSelectedProductIds([]);
    } else {
      setLookbookImages([]);
      setImages(content.images || []);
      setSelectedProductIds(content.productIds || []);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title || !description || !coverImage) {
      setMessage({ type: 'error', text: 'Please fill in all required fields.' });
      return;
    }

    setLoading(true);
    try {
      const contentData: any = {
        title,
        type,
        description,
        coverImage,
        featured,
        visible,
        publishDate: serverTimestamp(),
        updatedAt: serverTimestamp(),
        // Ensure optional fields are handled correctly (allow empty strings)
        season: season || '',
        subtitle: subtitle || '',
        author: author || ''
      };

      if (type === 'lookbook' || type === 'runway') {
        // Sanitize lookbook images to ensure no undefined values
        const sanitizedLookbookImages = lookbookImages.map(img => ({
          url: img.url,
          description: img.description || '',
          productIds: img.productIds || []
        }));
        
        contentData.lookbookImages = sanitizedLookbookImages;
        contentData.images = [];
        contentData.productIds = [];
      } else {
        contentData.images = images;
        contentData.productIds = selectedProductIds;
        contentData.lookbookImages = [];
      }

      if (editingId) {
        // Update existing
        await updateDoc(doc(db, 'magazine', editingId), contentData);
        setMessage({ type: 'success', text: 'Magazine content updated successfully!' });
      } else {
        // Create new
        contentData.createdAt = serverTimestamp();
        await addDoc(collection(db, 'magazine'), contentData);
        setMessage({ type: 'success', text: 'Magazine content created successfully!' });
      }

      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error saving magazine content:", error);
      setMessage({ type: 'error', text: 'Failed to save magazine content. Check console for details.' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this magazine content?')) return;

    setLoading(true);
    try {
      await deleteDoc(doc(db, 'magazine', id));
      setMessage({ type: 'success', text: 'Magazine content deleted successfully!' });
      fetchData();
    } catch (error) {
      console.error("Error deleting magazine content:", error);
      setMessage({ type: 'error', text: 'Failed to delete magazine content.' });
    } finally {
      setLoading(false);
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'lookbook': return 'Lookbook';
      case 'editorial': return 'Editorial';
      case 'campaign': return 'Campaign';
      default: return type;
    }
  };

  const getProductCount = (content: MagazineContent) => {
    if (content.type === 'lookbook' || content.type === 'runway') {
      if (!content.lookbookImages) return 0;
      const allIds = new Set<string>();
      content.lookbookImages.forEach(img => {
        img.productIds?.forEach(id => allIds.add(id));
      });
      return allIds.size;
    }
    return content.productIds?.length || 0;
  };

  return (
    <div className="p-8">
      <h2 className="text-3xl font-heading font-bold mb-8">Magazine Content Manager</h2>

      {message && (
        <div className={`mb-6 p-4 rounded ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {message.text}
        </div>
      )}

      {/* Form */}
      <div className="bg-white shadow-md rounded-lg p-6 mb-8">
        <h3 className="text-xl font-bold mb-4">{editingId ? 'Edit' : 'Create'} Magazine Content</h3>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Type *</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as 'lookbook' | 'editorial' | 'campaign' | 'runway')}
                className="w-full border border-gray-300 rounded px-3 py-2"
              >
                <option value="lookbook">Lookbook</option>
                <option value="editorial">Editorial</option>
                <option value="campaign">Campaign</option>
                <option value="runway">Runway</option>
              </select>
            </div>

            {(type === 'lookbook' || type === 'runway') && (
              <div>
                <label className="block text-sm font-medium mb-2">Season</label>
                <input
                  type="text"
                  value={season}
                  onChange={(e) => setSeason(e.target.value)}
                  placeholder="e.g., SS26, AW26"
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>
            )}

            {type === 'editorial' && (
              <div>
                <label className="block text-sm font-medium mb-2">Author</label>
                <input
                  type="text"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2">Subtitle</label>
              <input
                type="text"
                value={subtitle}
                onChange={(e) => setSubtitle(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Description *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 h-24"
              required
            />
          </div>

          {/* Cover Image */}
          <div>
            <label className="block text-sm font-medium mb-2">Cover Image *</label>
            <div className="max-w-xs">
              <ImageUploader
                value={coverImage}
                onChange={setCoverImage}
                pathPrefix="magazine/covers"
                aspectClass="aspect-[3/4]"
                onError={(text) => setMessage({ type: 'error', text })}
              />
            </div>
          </div>

          {/* Lookbook Images */}
          {(type === 'lookbook' || type === 'runway') && (
            <div className="border-t pt-6">
              <h4 className="text-lg font-bold mb-4">Lookbook Images</h4>

              <label className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded cursor-pointer hover:bg-blue-600 inline-flex mb-4">
                <Upload size={16} />
                Add Lookbook Images
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>

              {lookbookImages.length > 0 && (
                <div className="space-y-6">
                  {lookbookImages.map((img, idx) => (
                    <div key={idx} className="border rounded p-4 bg-gray-50">
                      <div className="flex items-start gap-4 mb-4">
                        <img src={img.url} alt={`Look ${idx + 1}`} className="h-32 w-auto object-cover rounded" />
                        <div className="flex-1">
                          <label className="block text-sm font-medium mb-2">Image {idx + 1} Description</label>
                          <textarea
                            value={img.description || ''}
                            onChange={(e) => updateLookbookImageDescription(idx, e.target.value)}
                            placeholder="Brief description for this look"
                            className="w-full border border-gray-300 rounded px-3 py-2 h-20"
                          />
                        </div>
                        <div className="flex flex-col gap-2">
                          <button
                            type="button"
                            onClick={() => removeLookbookImage(idx)}
                            className="p-2 bg-red-500 text-white rounded hover:bg-red-600 self-start"
                            title="Remove Image"
                          >
                            <Trash2 size={16} />
                          </button>
                          
                          <label className="p-2 bg-blue-500 text-white rounded hover:bg-blue-600 cursor-pointer self-start" title="Replace Image">
                            <Upload size={16} />
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleLookbookImageReplace(idx, e)}
                              className="hidden"
                            />
                          </label>
                        </div>
                      </div>

                      {/* Products for this image */}
                      <div>
                        <label className="block text-sm font-medium mb-2">Products in this look</label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-h-40 overflow-y-auto">
                          {products.map(product => (
                            <label key={product.id} className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={(img.productIds || []).includes(product.id)}
                                onChange={() => toggleLookbookImageProduct(idx, product.id)}
                              />
                              <span className="truncate">{product.title}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Editorial/Campaign Images */}
          {(type !== 'lookbook' && type !== 'runway') && (
            <div className="border-t pt-6">
              <h4 className="text-lg font-bold mb-4">Gallery Images</h4>

              <label className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded cursor-pointer hover:bg-blue-600 inline-flex mb-4">
                <Upload size={16} />
                Add Images
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>

              {images.length > 0 && (
                <div className="grid grid-cols-4 gap-4">
                  {images.map((img, idx) => (
                    <div key={idx} className="relative">
                      <img src={img} alt={`Image ${idx + 1}`} className="w-full h-32 object-cover rounded" />
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Global Products */}
              <div className="mt-6">
                <label className="block text-sm font-medium mb-2">Featured Products</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-h-40 overflow-y-auto border rounded p-3">
                  {products.map(product => (
                    <label key={product.id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={selectedProductIds.includes(product.id)}
                        onChange={() => toggleProductSelection(product.id)}
                      />
                      <span className="truncate">{product.title}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Flags */}
          <div className="flex gap-6 border-t pt-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={featured}
                onChange={(e) => setFeatured(e.target.checked)}
              />
              <span className="text-sm font-medium">Featured on homepage</span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={visible}
                onChange={(e) => setVisible(e.target.checked)}
              />
              <span className="text-sm font-medium">Visible to public</span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={loading || uploading}
              className="flex items-center gap-2 px-6 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
            >
              <Check size={16} />
              {editingId ? 'Update' : 'Create'}
            </button>

            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="flex items-center gap-2 px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                <X size={16} />
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Content List */}
      <div className="bg-white shadow-md rounded-lg p-6">
        <h3 className="text-xl font-bold mb-4">Existing Magazine Content</h3>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader className="animate-spin" size={32} />
          </div>
        ) : contents.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No magazine content yet.</p>
        ) : (
          <div className="space-y-4">
            {contents.map(content => (
              <div key={content.id} className="border rounded p-4 flex items-start gap-4 hover:bg-gray-50">
                <img src={content.coverImage} alt={content.title} className="w-24 h-32 object-cover rounded" />

                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-bold text-lg">{content.title}</h4>
                      <div className="flex items-center gap-3 text-sm text-gray-600 mt-1">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-bold">
                          {getTypeLabel(content.type)}
                        </span>
                        {content.season && <span>{content.season}</span>}
                        {content.author && <span>By {content.author}</span>}
                        {content.featured && (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-bold">
                            FEATURED
                          </span>
                        )}
                        {!content.visible && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-bold flex items-center gap-1">
                            <EyeOff size={12} /> HIDDEN
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(content)}
                        className="p-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(content.id)}
                        className="p-2 bg-red-500 text-white rounded hover:bg-red-600"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 mb-2">{content.description}</p>

                  <div className="text-xs text-gray-500">
                    {(content.type === 'lookbook' || content.type === 'runway') ? (
                      <span>{content.lookbookImages?.length || 0} looks</span>
                    ) : (
                      <span>{content.images?.length || 0} images</span>
                    )}
                    {' • '}
                    <span>{getProductCount(content)} products linked</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MagazineManager;
